import { getTreatment } from '@eventbrite/splitio-client';

/**
 * Split.io feature flag names mapped to their application keys.
 *
 * To add a new flag:
 * 1. Add entry here: `myNewFlag: 'split_io_flag_name'`
 * 2. Add default in `DEFAULT_FEATURE_FLAGS`
 * 3. Add evaluation in `evaluateFeatureFlags`
 */
const FLAGS = {} as const;

export type FeatureFlags = {
  [K in keyof typeof FLAGS]: boolean;
};

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {};

/**
 * Context passed to feature flag evaluation.
 */
export interface FeatureFlagContext {
  organizerId: string;
}

/**
 * Evaluates all feature flags for the given context.
 */
export async function evaluateFeatureFlags(ctx: FeatureFlagContext): Promise<FeatureFlags> {
  const flagEntries = await Promise.all(
    (Object.entries(FLAGS) as [keyof FeatureFlags, string][]).map(async ([flagKey, flagName]) => {
      const value = await evaluateFlag(flagName, ctx.organizerId, {
        organizerId: ctx.organizerId,
      });
      return [flagKey, value] as const;
    }),
  );

  return flagEntries.reduce(
    (acc, [flagKey, value]) => ({ ...acc, [flagKey]: value }),
    DEFAULT_FEATURE_FLAGS,
  );
}

/**
 * Evaluates a single feature flag.
 */
async function evaluateFlag(
  flagName: string,
  key: string,
  attributes: Record<string, string | undefined>,
): Promise<boolean> {
  try {
    const cleanAttributes: Record<string, string> = {};
    for (const [attrKey, value] of Object.entries(attributes)) {
      if (value !== undefined) cleanAttributes[attrKey] = value;
    }
    const treatment = await getTreatment(key, flagName, cleanAttributes);
    return treatment === 'on';
  } catch (error) {
    console.warn(`Failed to evaluate feature flag: ${flagName}`, { error, key });
    const flagKey = (Object.entries(FLAGS) as [keyof FeatureFlags, string][]).find(
      ([, value]) => value === flagName,
    )?.[0];
    return (flagKey !== undefined ? DEFAULT_FEATURE_FLAGS[flagKey] : undefined) ?? false;
  }
}
